/**
 * Envelo Mail Admin API Client
 *
 * Talks to the FastAPI admin backend at /api and /jmap.
 * Maintains JMAP-compatible method calls for domain/account/DKIM management.
 */

const STALWART_API = '/jmap'

export interface StalwartAuth {
  username: string
  password: string
}

export interface JmapResponse {
  methodResponses: [string, unknown, string][]
  created?: Record<string, unknown>
  updated?: Record<string, string[]>
  destroyed?: string[]
  notCreated?: Record<string, unknown>
  notUpdated?: Record<string, unknown>
  notDestroyed?: Record<string, unknown>
}

// ── Auth ──

// JMAP Mail Auth - validates against jmap-proxy /session endpoint
// This is different from admin auth (which validates against /api/account)

export async function jmapAuth(credentials: { username: string; password: string }): Promise<boolean> {
  const res = await fetch('/session', {
    headers: { Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}` },
  })
  return res.ok
}

// Admin auth - validates against FastAPI admin backend
export async function stalwartAuth(auth: StalwartAuth): Promise<boolean> {
  const res = await fetch('/api/account', {
    headers: { Authorization: `Basic ${btoa(`${auth.username}:${auth.password}`)}` },
  })
  return res.ok
}

// ── JMAP method call helper ──

async function jmapCall(
  auth: StalwartAuth,
  method: string,
  args: Record<string, unknown> = {},
  using: string[] = ['urn:ietf:params:jmap:core', 'urn:stalwart:jmap']
): Promise<Record<string, unknown>> {
  const callId = crypto.randomUUID()
  const res = await fetch(STALWART_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Basic ${btoa(`${auth.username}:${auth.password}`)}`,
    },
    body: JSON.stringify({
      using,
      methodCalls: [[method, args, callId]],
    }),
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Admin API error ${res.status}: ${text}`)
  }

  const data: JmapResponse = await res.json()
  const result = data.methodResponses?.[0]
  if (!result) throw new Error('Empty response from Admin API')

  const payload = result[1] as Record<string, unknown>
  if (payload?.type === 'error') {
    throw new Error((payload.description as string) || 'Unknown API error')
  }
  return payload
}

// ── Domains ──

export interface Domain {
  id: string
  name: string
  description?: string
  enabled: boolean
}

export async function listDomains(auth: StalwartAuth): Promise<Domain[]> {
  const result = await jmapCall(auth, 'x:Domain/query', {
    filter: {},
    limit: 1000,
  })
  const ids = (result as { ids?: string[] }).ids || []
  if (ids.length === 0) return []

  const domains = await jmapCall(auth, 'x:Domain/get', { ids })
  return (domains as { list?: Domain[] }).list || []
}

export async function getDomain(auth: StalwartAuth, id: string): Promise<Domain | null> {
  const result = await jmapCall(auth, 'x:Domain/get', { ids: [id] })
  const list = (result as { list?: Domain[] }).list || []
  return list[0] || null
}

export async function createDomain(
  auth: StalwartAuth,
  name: string,
  description?: string
): Promise<string> {
  const result = await jmapCall(auth, 'x:Domain/set', {
    create: {
      new1: {
        '@type': 'Domain',
        name,
        description: description || '',
        enabled: true,
      },
    },
  })
  const created = (result as { created?: Record<string, { id: string }> }).created
  if (!created?.new1) throw new Error('Failed to create domain')
  return created.new1.id
}

export async function deleteDomain(auth: StalwartAuth, id: string): Promise<void> {
  await jmapCall(auth, 'x:Domain/set', {
    destroy: [id],
  })
}

// ── DNS Records ──

export interface DnsRecord {
  type: string
  host: string
  value: string
  priority?: number
}

export async function getDnsRecords(
  auth: StalwartAuth,
  domain: string
): Promise<DnsRecord[]> {
  const res = await fetch(`/api/dns/records/${domain}`, {
    headers: { Authorization: `Basic ${btoa(`${auth.username}:${auth.password}`)}` },
  })
  if (!res.ok) return []
  const data = await res.json()
  return (data as DnsRecord[]) || []
}

// ── Accounts ──

export interface Account {
  id: string
  name: string
  '@type': string
  domainId?: string
  enabled: boolean
  description?: string
}

export async function listAccounts(
  auth: StalwartAuth,
  domainId?: string
): Promise<Account[]> {
  const filter: Record<string, unknown> = {}
  if (domainId) filter.domainId = domainId

  const result = await jmapCall(auth, 'x:Account/query', {
    filter,
    limit: 1000,
  })
  const ids = (result as { ids?: string[] }).ids || []
  if (ids.length === 0) return []

  const accounts = await jmapCall(auth, 'x:Account/get', { ids })
  return (accounts as { list?: Account[] }).list || []
}

export async function createAccount(
  auth: StalwartAuth,
  params: {
    name: string
    domainId: string
    password?: string
    description?: string
  }
): Promise<string> {
  const result = await jmapCall(auth, 'x:Account/set', {
    create: {
      new1: {
        '@type': 'User',
        name: params.name,
        domainId: params.domainId,
        description: params.description || '',
        enabled: true,
        credentials: params.password ? { password: params.password } : {},
        memberGroupIds: {},
        roles: { '@type': 'User' },
        permissions: { '@type': 'Inherit' },
        quotas: {},
        aliases: {},
        encryptionAtRest: { '@type': 'Disabled' },
      },
    },
  })
  const created = (result as { created?: Record<string, { id: string }> }).created
  if (!created?.new1) throw new Error('Failed to create account')
  return created.new1.id
}

export async function deleteAccount(auth: StalwartAuth, id: string): Promise<void> {
  await jmapCall(auth, 'x:Account/set', {
    destroy: [id],
  })
}

export async function updateAccount(
  auth: StalwartAuth,
  id: string,
  changes: Record<string, unknown>
): Promise<void> {
  await jmapCall(auth, 'x:Account/set', {
    update: { [id]: changes },
  })
}

// ── DKIM ──

export interface DkimSignature {
  id: string
  domain: string
  selector: string
  enabled: boolean
}

export async function listDkim(auth: StalwartAuth): Promise<DkimSignature[]> {
  const result = await jmapCall(auth, 'x:DkimSignature/query', {
    filter: {},
    limit: 1000,
  })
  const ids = (result as { ids?: string[] }).ids || []
  if (ids.length === 0) return []

  const dkim = await jmapCall(auth, 'x:DkimSignature/get', { ids })
  return (dkim as { list?: DkimSignature[] }).list || []
}

export async function createDkim(
  auth: StalwartAuth,
  domain: string,
  algorithm: 'ed25519' | 'rsa' = 'ed25519'
): Promise<void> {
  await fetch('/api/dkim', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Basic ${btoa(`${auth.username}:${auth.password}`)}`,
    },
    body: JSON.stringify({ domain, algorithm }),
  })
}

// ── Server Info ──

export interface ServerAccount {
  permissions: string[]
  edition: string
  locale: string
}

export async function getServerAccountInfo(
  auth: StalwartAuth
): Promise<ServerAccount> {
  const res = await fetch('/api/account', {
    headers: { Authorization: `Basic ${btoa(`${auth.username}:${auth.password}`)}` },
  })
  return res.json()
}
